import { Preferences } from '@capacitor/preferences';

/**
 * `amazon-cognito-identity-js`'s `ICognitoStorage` — getItem/setItem/
 * removeItem/clear are all strictly synchronous (no `Promise` anywhere in
 * its own `index.d.ts`), which `@capacitor/preferences`'s Promise-based API
 * can't satisfy directly. Duplicated here (rather than imported) so this
 * file has no compile-time dependency on `amazon-cognito-identity-js`.
 */
interface ICognitoStorage {
  setItem(key: string, value: string): void;
  getItem(key: string): string | null;
  removeItem(key: string): void;
  clear(): void;
}

export interface CognitoPreferencesStorage extends ICognitoStorage {
  /** Resolves once the in-memory cache has been hydrated from Preferences
   *  (and, on first run only, migrated from legacy window.localStorage).
   *  Memoized — safe and cheap to await redundantly from any call site
   *  that's about to read storage synchronously (see AuthService.
   *  restoreSession(), which awaits this immediately before the one
   *  synchronous storage read amazon-cognito-identity-js itself makes). */
  hydrate(): Promise<void>;
  /** Test-only: clears the in-memory cache and hydration memo. */
  __resetForTesting(): void;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/**
 * A synchronous-read/write cache in front of `@capacitor/preferences`,
 * shaped to satisfy `amazon-cognito-identity-js`'s `Storage` option so
 * Cognito's own session tokens land in Preferences (iOS UserDefaults /
 * Android SharedPreferences) instead of raw `window.localStorage` — which a
 * Capacitor WKWebView/Android WebView can opportunistically evict under OS
 * storage pressure (see `@capacitor/preferences`'s own README).
 *
 * `prefix` scopes which keys `hydrate()`/legacy migration look at; Cognito
 * always writes keys shaped `CognitoIdentityServiceProvider.<clientId>.*`,
 * and this store never needs to know the exact key names — just the shared
 * prefix — since it's a generic passthrough over whatever Cognito writes.
 */
export function createCognitoPreferencesStorage(prefix: string): CognitoPreferencesStorage {
  const cache = new Map<string, string>();
  let hydratePromise: Promise<void> | null = null;

  /** Copies any pre-existing raw localStorage keys (written by Cognito's own
   *  default storage before this adapter existed) into Preferences, then
   *  removes them — self-terminating, same shape as idb-kv-store.util.ts's
   *  legacy migration. Never overwrites a key Preferences already has. */
  async function migrateLegacyLocalStorage(): Promise<void> {
    let keys: string[];
    try {
      keys = Object.keys(window.localStorage).filter((k) => k.startsWith(prefix));
    } catch {
      return; // localStorage disabled/inaccessible — nothing to migrate.
    }
    for (const key of keys) {
      if (cache.has(key)) continue;
      let value: string | null;
      try {
        value = window.localStorage.getItem(key);
      } catch {
        continue;
      }
      if (value == null) continue;
      cache.set(key, value);
      try {
        await Preferences.set({ key, value });
        window.localStorage.removeItem(key);
      } catch {
        // Preferences write failed — cache still reflects the migrated
        // value for this session; legacy key stays in place so a later
        // successful hydrate() can retry.
      }
    }
  }

  function hydrate(): Promise<void> {
    if (!isBrowser()) return Promise.resolve();
    if (hydratePromise) return hydratePromise;
    hydratePromise = (async () => {
      try {
        const { keys } = await Preferences.keys();
        for (const key of keys) {
          if (!key.startsWith(prefix)) continue;
          const { value } = await Preferences.get({ key });
          if (value != null) cache.set(key, value);
        }
        await migrateLegacyLocalStorage();
      } catch (err) {
        console.warn('[cognito-preferences-storage] hydrate failed', err);
      }
    })();
    return hydratePromise;
  }

  return {
    hydrate,

    getItem(key: string): string | null {
      return cache.get(key) ?? null;
    },

    setItem(key: string, value: string): void {
      cache.set(key, value);
      if (!isBrowser()) return;
      Preferences.set({ key, value }).catch((err) => console.warn('[cognito-preferences-storage] setItem failed', err));
    },

    removeItem(key: string): void {
      cache.delete(key);
      if (!isBrowser()) return;
      Preferences.remove({ key }).catch((err) => console.warn('[cognito-preferences-storage] removeItem failed', err));
    },

    clear(): void {
      // Never call Preferences.clear() here — on native that wipes the
      // *entire* Preferences store, not just this adapter's keys. Remove
      // only the keys this adapter itself is holding.
      const keys = Array.from(cache.keys());
      cache.clear();
      if (!isBrowser()) return;
      for (const key of keys) {
        Preferences.remove({ key }).catch((err) => console.warn('[cognito-preferences-storage] clear failed', err));
      }
    },

    __resetForTesting(): void {
      cache.clear();
      hydratePromise = null;
    },
  };
}

/**
 * Single shared instance — CognitoUserPool is effectively a singleton too
 * (CognitoService is registered once in app.config.ts). Exported directly
 * rather than owned as a CognitoService field so the boot-time
 * APP_INITIALIZER (app.config.ts) can hydrate it without forcing
 * CognitoService's otherwise-lazy construction.
 */
export const cognitoPreferencesStorage = createCognitoPreferencesStorage('CognitoIdentityServiceProvider.');

/**
 * A second, separately-prefixed instance for small non-Cognito scalars that
 * belong in native Preferences rather than blob-oriented IndexedDB (see
 * idb-kv-store.util.ts's header: that store exists for blob-heavy data
 * exceeding localStorage's quota, the opposite case). Used for AuthService's
 * VIEW_KEY ('guest'|'host' UI toggle). Its own distinct prefix (rather than
 * an empty one) keeps its hydrate()/legacy-scan/clear() from ever touching
 * `cognitoPreferencesStorage`'s keys or unrelated localStorage entries —
 * each instance only ever sees its own namespace.
 */
export const APP_PREF_PREFIX = 'cnt-app-pref.';
export const appPreferencesStorage = createCognitoPreferencesStorage(APP_PREF_PREFIX);
